import React, { useState, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { doc, getDoc } from "firebase/firestore";
import { db, auth } from "../config/firebase";

const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [approved, setApproved] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const checkApprovalStatus = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Get the user document from Firestore
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          // Check if the user is approved or is an admin (admins are always approved)
          const userData = userDoc.data();
          const isApproved = userData.approved === true || userData.role === 'admin' || userData.isAdmin === true;
          setApproved(isApproved);
          setUserRole(userData.role);
        } else {
          setApproved(false);
          setUserRole(null);
        }
      } catch (error) {
        console.error("Error checking approval status:", error);
        setApproved(false);
        setUserRole(null);
      } finally {
        setLoading(false);
      }
    };

    checkApprovalStatus();
  }, [user]);

  console.log("Protected Route - User:", user?.email, "Approved:", approved, "Role:", userRole);
  
  // Show loading state while checking approval status
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Check if user is authenticated
  if (!user) {
    console.log("No user found, redirecting to login");
    return <Navigate to="/login" />;
  }

  // Check if user is approved
  if (!approved) {
    console.log("User not approved, showing access denied");
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-6">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow-md p-8 border border-gray-100 dark:border-gray-700">
          <div className="text-center">
            <svg 
              className="mx-auto h-12 w-12 text-yellow-500" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
              />
            </svg>
            <h2 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">
              Account Pending Approval
            </h2>
            <p className="mt-2 text-gray-600 dark:text-gray-300">
              Your account is pending approval by an administrator. Please check back later or contact support for assistance.
            </p>
            <div className="mt-6">
              <button
                onClick={() => auth.signOut()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if the user has access to the current route based on their role
  const currentPath = location.pathname;
  
  // Pages that only full admins can access
  const adminOnlyPages = [
    '/admin/users',
    '/admin/seed',
    '/admin/contacts'
  ];

  // Pages that only admins and gym admins can access
  const gymAdminPages = [
    '/admin/events',
    '/admin/updates'
  ];
  
  // Pages that only admins and gym admins can access (no coaches or judges)
  const adminAndGymAdminOnlyPages = [
    '/admin/bids',
    '/admin/bid-voting'
  ];
  
  // Check if the current path is an admin-only page
  const isAdminOnlyPage = adminOnlyPages.some(page => currentPath.startsWith(page));
  
  // Check if the current path is a gym admin page
  const isGymAdminPage = gymAdminPages.some(page => currentPath.startsWith(page));
  
  // Check if the current path is a page only admins and gym admins can access
  const isAdminAndGymAdminOnlyPage = adminAndGymAdminOnlyPages.some(page => currentPath.startsWith(page));
  
  // Redirect logic based on user role and requested page
  if (isAdminOnlyPage && userRole !== 'admin') {
    // Only admins can access admin-only pages
    console.log("User doesn't have admin privileges for this page, redirecting to home");
    return <Navigate to="/" />;
  } else if (isGymAdminPage && userRole !== 'admin' && userRole !== 'gym_admin') {
    // Only admins and gym admins can access gym admin pages
    console.log("User doesn't have gym admin privileges for this page, redirecting to home");
    return <Navigate to="/" />;
  } else if (isAdminAndGymAdminOnlyPage && (userRole === 'judge' || userRole === 'coach')) {
    // Coaches and judges cannot access championship bid pages
    console.log("Coaches and judges cannot access championship bid pages, redirecting to home");
    return <Navigate to="/" />;
  } else if (currentPath === '/admin/bids' && userRole === 'gym_admin') {
    // Redirect gym admins to the voting page instead of the full bid manager
    console.log("Redirecting gym admin to bid voting page");
    return <Navigate to="/admin/bid-voting" />;
  } else if (currentPath === '/admin/bid-voting/manage' && userRole !== 'admin') {
    // Only full admins can access this special route
    console.log("User doesn't have admin privileges for bid management, redirecting to voting page");
    return <Navigate to="/admin/bid-voting" />;
  }
  
  console.log("User authenticated and approved, allowing access");
  return children;
};

export default ProtectedRoute;
